import React from 'react';
import { Check, Star } from 'lucide-react';
import { formatMoney } from '../../lib/formatMoney';

const money = (n) => formatMoney(n || 0, { decimals: 0 });
const API = process.env.REACT_APP_BACKEND_URL;

export default function PackagesShowcase({ packages, selectedName, defaultOpen = true, token = null, canChoose = false }) {
  const list = Array.isArray(packages) ? packages.filter((p) => p && p.name) : [];
  const [open, setOpen] = React.useState(Boolean(defaultOpen));
  if (list.length === 0) return null;
  const sel = (selectedName || '').trim().toLowerCase();
  const [requested, setRequested] = React.useState(() => new Set());
  const [sending, setSending] = React.useState(false);
  const choosePackage = (p) => {
    const nm = (p && p.name ? String(p.name).trim() : '');
    if (!nm) return;
    const keyl = nm.toLowerCase();
    if (requested.has(keyl)) return;
    setRequested((prev) => { const n = new Set(prev); n.add(keyl); return n; });
    if (token && API) {
      setSending(true);
      fetch(`${API}/api/leads/client-view/${encodeURIComponent(token)}/package-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nm, priceLabel: p.priceLabel || '', amount: (p.amount != null ? String(p.amount) : '') }),
      }).catch(() => {}).finally(() => setSending(false));
    }
  };

  return (
    <div className="mt-6 rounded-[22px] p-6 sm:p-7" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))', border: '1px solid rgba(212,175,55,0.16)', boxShadow: '0 4px 24px rgba(0,0,0,0.32), 0 0 0 1px rgba(212,175,55,0.03)' }} data-testid="packages-showcase" role="region" aria-label="Our packages">
      <div>
        <p className="text-[10px] uppercase tracking-[0.26em] text-[#D4AF37]/70 font-medium">Our Packages</p>
        <h3 className="mt-1.5 text-[18px] sm:text-[20px] font-semibold text-white tracking-tight" data-testid="packages-showcase-title">What We Offer</h3>
        <p className="mt-1 text-[13px] text-white/45 leading-relaxed max-w-md">A look at our signature packages. Your proposed option is highlighted — we are happy to tailor any of these to your day.</p>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-3 text-[12px] font-medium text-[#D4AF37]/80 hover:text-[#D4AF37] transition-colors"
          data-testid="packages-showcase-toggle"
        >
          {open ? 'Hide packages' : 'View packages'}
        </button>
      </div>

      <div className={(open ? 'mt-5 grid' : 'hidden') + ' grid-cols-1 sm:grid-cols-2 gap-4'} data-testid="packages-showcase-grid">
        {list.map((p, i) => {
          const isSel = sel && (p.name || '').trim().toLowerCase() === sel;
          const price = p.priceLabel || (p.amount != null ? money(p.amount) : '');
          return (
            <div key={i} className="relative rounded-2xl overflow-hidden flex flex-col" style={{ background: isSel ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.02)', border: '1px solid ' + (isSel ? 'rgba(212,175,55,0.35)' : 'rgba(255,255,255,0.06)') }} data-testid={'showcase-package-' + i}>
              {p.photo ? (
                <div className="w-full h-32 bg-white/[0.03]">
                  <img src={p.photo} alt="" className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-[15px] font-semibold text-white leading-snug">{p.name}</h4>
                  {p.popular ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[0.16em] uppercase flex-shrink-0" style={{ background: 'linear-gradient(180deg, #E6C86A, #B88B1A)', color: '#0B0F1A' }}><Star className="w-2.5 h-2.5" />Popular</span>
                  ) : null}
                </div>
                {isSel ? (
                  <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-[#E7C96A]"><Check className="w-3 h-3" />Your proposed package</span>
                ) : null}
                {p.description ? (<p className="mt-1.5 text-[12px] text-white/45 leading-relaxed">{p.description}</p>) : null}
                {Array.isArray(p.included) && p.included.length > 0 ? (
                  <ul className="mt-2.5 space-y-1">
                    {p.included.slice(0, 6).map((line, j) => (
                      <li key={j} className="flex items-start gap-1.5 text-[12px] text-white/55">
                        <Check className="w-3 h-3 text-[#D4AF37]/70 mt-0.5 flex-shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {price ? (
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="text-[13px] font-semibold text-[#D4AF37]/85 tabular-nums">{price}</span>
                  </div>
                ) : null}
                {isSel ? (
                  <div className="mt-3 w-full text-center rounded-xl px-3 py-2.5 text-[12px] font-semibold" style={{ background: 'rgba(52,199,89,0.06)', border: '1px solid rgba(52,199,89,0.22)', color: '#8FE6A6' }}>● This is your proposal</div>
                ) : canChoose ? (
                  requested.has((p.name || '').trim().toLowerCase()) ? (
                    <div className="mt-3 w-full text-center rounded-xl px-3 py-2.5 text-[12px] font-semibold" style={{ background: 'rgba(52,199,89,0.06)', border: '1px solid rgba(52,199,89,0.22)', color: '#8FE6A6' }} data-testid={'showcase-package-requested-' + i}>✓ Request sent</div>
                  ) : (
                    <button type="button" onClick={() => choosePackage(p)} disabled={sending} className="mt-3 w-full text-center rounded-xl px-3 py-2.5 text-[12.5px] font-semibold transition-colors disabled:opacity-50" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.35)', color: '#E7C75E' }} data-testid={'showcase-package-choose-' + i}>Choose this instead</button>
                  )
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {requested.size > 0 ? (
        <p className="mt-4 text-center text-[12px] font-medium" style={{ color: '#8FE6A6' }} data-testid="showcase-request-note">Request sent — we’ll update your proposal shortly. Nothing is charged; this just lets us know.</p>
      ) : (
        <p className="mt-4 text-center text-[11px] text-white/30 leading-relaxed">Final pricing and details are confirmed with you directly.</p>
      )}
    </div>
  );
}
